import { COMMERCE_EVENT_TYPES } from '@cartly/shared';
import { Alert, Badge, Button, Card, EmptyState, Modal, Spinner, useToast } from '@cartly/ui';
import { Activity, Inbox, Package } from 'lucide-react';
import { useState } from 'react';
import { api, errorMessage } from '../../api/client';
import type { EventDetail, EventRow, Page, ProductRow, ReceiptRow } from '../../api/types';
import { JsonView, LoadMore, StatusBadge } from '../../components/Common';
import { formatDateTime, formatMoney, formatRelative } from '../../app/format';
import { useLoader, usePaged } from '../../app/hooks';

const SOURCE_LABELS: Record<string, string> = {
  api: 'REST API',
  sdk_node: 'Node SDK',
  sdk_php: 'PHP SDK',
  sdk_python: 'Python SDK',
  browser: 'Browser',
  webhook: 'Signed webhook',
  woocommerce: 'WooCommerce',
  shopify: 'Shopify',
};

function EventModal({
  id,
  canReplay,
  onClose,
  onReplayed,
}: {
  id: string;
  canReplay: boolean;
  onClose: () => void;
  onReplayed: () => void;
}) {
  const toast = useToast();
  const { data, error, loading } = useLoader(() => api.get<EventDetail>(`/events/${id}`), id);
  const [replaying, setReplaying] = useState(false);

  const replay = async () => {
    setReplaying(true);
    try {
      await api.post(`/events/${id}/replay`);
      toast.success('Event queued for processing');
      onReplayed();
      onClose();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setReplaying(false);
    }
  };

  return (
    <Modal
      open
      title="Event details"
      onClose={onClose}
      footer={
        data?.status === 'failed' && canReplay ? (
          <Button variant="primary" loading={replaying} onClick={() => void replay()}>
            Replay event
          </Button>
        ) : undefined
      }
    >
      {loading && !data ? (
        <Spinner />
      ) : error || !data ? (
        <Alert tone="critical">{error}</Alert>
      ) : (
        <div className="stack">
          <dl className="dl">
            <dt>Type</dt>
            <dd className="mono">{data.type}</dd>
            <dt>Status</dt>
            <dd>
              <StatusBadge
                status={data.status}
                label={data.status === 'pending' && data.attempts > 0 ? 'retrying' : undefined}
              />{' '}
              {data.attempts > 1 && (
                <span className="text-muted text-sm">after {data.attempts} attempts</span>
              )}
            </dd>
            <dt>External ID</dt>
            <dd className="mono">{data.externalId ?? '—'}</dd>
            <dt>Source</dt>
            <dd>{SOURCE_LABELS[data.source] ?? data.source}</dd>
            <dt>Occurred</dt>
            <dd>{formatDateTime(data.occurredAt)}</dd>
            <dt>Idempotency key</dt>
            <dd className="mono text-sm break">{data.idempotencyKey}</dd>
          </dl>
          {data.lastError && (
            <Alert tone={data.status === 'failed' ? 'critical' : 'warning'} title="Last error">
              {data.lastError}
            </Alert>
          )}
          {data.deliveries.length > 0 && (
            <div className="stack-sm">
              <span className="field__label">Webhook deliveries</span>
              {data.deliveries.map((d) => (
                <div key={d.id} className="row-between text-sm">
                  <span className="mono break">{d.url}</span>
                  <StatusBadge status={d.status} />
                </div>
              ))}
            </div>
          )}
          <span className="field__label">Payload</span>
          <JsonView value={data.payload} />
        </div>
      )}
    </Modal>
  );
}

export function StoreEvents({ storeId, canReplay }: { storeId: string; canReplay: boolean }) {
  const [status, setStatus] = useState('');
  const [type, setType] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const query = new URLSearchParams({
    limit: '50',
    ...(status && { status }),
    ...(type && { type }),
  });
  const list = usePaged(
    (cursor) =>
      api.get<Page<EventRow>>(
        `/stores/${storeId}/events?${query}${cursor ? `&cursor=${cursor}` : ''}`,
      ),
    `${storeId}:${status}:${type}`,
  );

  return (
    <Card
      title="Event log"
      subtitle="Every normalized event received for this store. Failed events can be replayed."
      actions={
        <div className="row">
          <select
            className="select select--inline"
            aria-label="Filter by status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">All statuses</option>
            <option value="failed">Failed</option>
            <option value="pending">Pending</option>
            <option value="processed">Processed</option>
          </select>
          <select
            className="select select--inline"
            aria-label="Filter by type"
            value={type}
            onChange={(e) => setType(e.target.value)}
          >
            <option value="">All types</option>
            {COMMERCE_EVENT_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
      }
      flush
    >
      {list.loading && list.items.length === 0 ? (
        <div className="card__body">
          <Spinner />
        </div>
      ) : list.error ? (
        <div className="card__body">
          <Alert tone="critical">{list.error}</Alert>
        </div>
      ) : list.items.length === 0 ? (
        <EmptyState icon={<Activity size={22} />} title="No events">
          {status || type
            ? 'Nothing matches these filters.'
            : 'Events appear here as soon as your store sends them.'}
        </EmptyState>
      ) : (
        <>
          <div className="table-wrap">
            <table className="table table--clickable">
              <thead>
                <tr>
                  <th>Event</th>
                  <th>External ID</th>
                  <th>Source</th>
                  <th>Status</th>
                  <th>Occurred</th>
                </tr>
              </thead>
              <tbody>
                {list.items.map((e) => (
                  <tr
                    key={e.id}
                    onClick={() => setOpen(e.id)}
                    tabIndex={0}
                    onKeyDown={(k) => k.key === 'Enter' && setOpen(e.id)}
                  >
                    <td className="mono text-sm">{e.type}</td>
                    <td className="mono text-sm">{e.externalId ?? '—'}</td>
                    <td className="text-sm">{SOURCE_LABELS[e.source] ?? e.source}</td>
                    <td>
                      <StatusBadge
                        status={e.status}
                        label={e.status === 'pending' && e.attempts > 0 ? 'retrying' : undefined}
                      />
                    </td>
                    <td className="text-muted text-sm">{formatRelative(e.occurredAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {list.hasMore && (
            <LoadMore onClick={() => void list.loadMore()} loading={list.loadingMore} />
          )}
        </>
      )}
      {open && (
        <EventModal
          id={open}
          canReplay={canReplay}
          onClose={() => setOpen(null)}
          onReplayed={() => void list.reload()}
        />
      )}
    </Card>
  );
}

export function StoreProducts({ storeId }: { storeId: string }) {
  const list = usePaged(
    (cursor) =>
      api.get<Page<ProductRow>>(
        `/products?storeId=${storeId}&limit=50${cursor ? `&cursor=${cursor}` : ''}`,
      ),
    storeId,
  );
  return (
    <Card title="Products" subtitle="Catalog synchronized from this store." flush>
      {list.loading && list.items.length === 0 ? (
        <div className="card__body">
          <Spinner />
        </div>
      ) : list.items.length === 0 ? (
        <EmptyState icon={<Package size={22} />} title="No products yet">
          Send <code>product.created</code> events or connect WooCommerce product webhooks.
        </EmptyState>
      ) : (
        <>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Variants</th>
                  <th>From</th>
                  <th>Inventory</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {list.items.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <div className="table__primary">{p.title}</div>
                      <div className="table__secondary mono">{p.externalId}</div>
                    </td>
                    <td>{p.variantCount}</td>
                    <td>{formatMoney(p.priceMin, p.store.currency)}</td>
                    <td>
                      {p.inventory === null ? (
                        <span className="text-muted">Not tracked</span>
                      ) : (
                        p.inventory
                      )}
                    </td>
                    <td>
                      <StatusBadge status={p.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {list.hasMore && (
            <LoadMore onClick={() => void list.loadMore()} loading={list.loadingMore} />
          )}
        </>
      )}
    </Card>
  );
}

export function StoreReceipts({ storeId }: { storeId: string }) {
  const list = usePaged(
    (cursor) =>
      api.get<Page<ReceiptRow>>(
        `/stores/${storeId}/receipts?limit=50${cursor ? `&cursor=${cursor}` : ''}`,
      ),
    storeId,
  );
  return (
    <Card
      title="Webhook log"
      subtitle="Raw deliveries received from your platform, including rejected ones."
      flush
    >
      {list.loading && list.items.length === 0 ? (
        <div className="card__body">
          <Spinner />
        </div>
      ) : list.items.length === 0 ? (
        <EmptyState icon={<Inbox size={22} />} title="No webhook deliveries">
          Deliveries to this store's webhook URLs appear here. Events sent through the REST API or
          SDKs are listed under Events.
        </EmptyState>
      ) : (
        <>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Received</th>
                  <th>Source</th>
                  <th>Topic</th>
                  <th>Signature</th>
                  <th>Result</th>
                  <th>Events</th>
                </tr>
              </thead>
              <tbody>
                {list.items.map((r) => (
                  <tr key={r.id}>
                    <td className="text-sm">{formatDateTime(r.receivedAt)}</td>
                    <td className="text-sm">
                      {r.provider === 'woocommerce' ? 'WooCommerce' : 'Signed webhook'}
                    </td>
                    <td className="mono text-sm">{r.topic}</td>
                    <td>
                      {r.signatureValid ? (
                        <Badge tone="success">valid</Badge>
                      ) : (
                        <Badge tone="critical">invalid</Badge>
                      )}
                    </td>
                    <td>
                      <StatusBadge status={r.status} />
                      {r.error && <div className="table__secondary">{r.error}</div>}
                    </td>
                    <td>{r.eventCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {list.hasMore && (
            <LoadMore onClick={() => void list.loadMore()} loading={list.loadingMore} />
          )}
        </>
      )}
    </Card>
  );
}
